import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Modal, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const checkoutStyles = `
  .promotion-banner {
    background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
    border-radius: 12px;
    padding: 16px;
    margin: 8px 0;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    border: 1px solid rgba(255,255,255,0.3);
  }
  
  .promotion-header {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
  }
  
  .promotion-icon {
    font-size: 1.2rem;
    margin-right: 8px;
    color: #ff9800;
  }
  
  .promotion-title {
    color: #1976d2;
    font-weight: 600;
    font-size: 1rem;
    margin: 0;
  }
  
  .promotion-details {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  
  .promotion-discount {
    display: flex;
    align-items: center;
    color: #1976d2;
  }
  
  .promotion-discount-icon {
    font-size: 0.9rem;
    margin-right: 6px;
    color: #ff9800;
  }
  
  .promotion-discount-text {
    font-weight: 600;
  }
  
  .promotion-description {
    text-align: center;
    color: #666;
    font-size: 0.9rem;
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .promotion-description-icon {
    font-size: 0.8rem;
    margin-right: 6px;
    color: #ffc107;
  }
  
  .payment-option-unselected {
    border: 2px solid #e9ecef !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  }
  
  .payment-option-unselected:hover {
    border-color: #007bff !important;
    background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%) !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,123,255,0.15);
  }
  
  .payment-option-selected {
    border: 2px solid #007bff !important;
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%) !important;
    color: white !important;
    box-shadow: 0 4px 16px rgba(0,123,255,0.3);
    transform: translateY(-1px);
  }
  
  .payment-selection-hint {
    text-align: center;
    color: #6c757d;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 12px;
    font-size: 0.95rem;
    border-left: 4px solid #007bff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }
  
  .payment-option {
    border-radius: 12px !important;
    padding: 1rem !important;
    margin-bottom: 0.75rem;
    cursor: pointer;
    position: relative;
    overflow: hidden;
  }
  
  .payment-option::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }
  
  .payment-option:hover::before {
    left: 100%;
  }
  
  .payment-option i {
    font-size: 1.2rem;
    margin-right: 0.75rem;
  }
  
  .payment-option span {
    font-weight: 500;
    font-size: 1rem;
  }
`;

const Checkout = () => {
  const navigate = useNavigate();
  
  const [cart, setCart] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [tableNumber, setTableNumber] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(true); // Показываем способы оплаты сразу
  const [order, setOrder] = useState(null);
  const [orderSummary, setOrderSummary] = useState(null);
  const [orderType, setOrderType] = useState('dine_in');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryEntrance, setDeliveryEntrance] = useState('');
  const [deliveryFloor, setDeliveryFloor] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  const [statusTimer, setStatusTimer] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [paid, setPaid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDebug, setPaymentDebug] = useState(null);
  const [theme, setTheme] = useState(null);

  const savedMenuForPayment = (() => {
    try {
      const raw = localStorage.getItem('menu');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const menuPaymentOptions = savedMenuForPayment?.paymentOptions || {};
  const allowCashDelivery = menuPaymentOptions.cash !== false;
  const allowExpressPayDelivery = menuPaymentOptions.expresspay !== false;
  const showExpressPayDelivery = allowExpressPayDelivery && !!(savedMenuForPayment?.expressPayToken);
  const [orderStatus, setOrderStatus] = useState('pending');
  const [showStatusMessage, setShowStatusMessage] = useState(false);
  const [isOrderSubmitted, setIsOrderSubmitted] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState('');
  const [deliveryTimeApproved, setDeliveryTimeApproved] = useState(null);
  const [showDeliveryTimeApproval, setShowDeliveryTimeApproval] = useState(false);
  const [callBack, setCallBack] = useState(false);
  
  // Состояния для акций
  const [promotions, setPromotions] = useState([]);
  const [appliedPromotions, setAppliedPromotions] = useState([]);

  // Цветовая схема
  const colors = {
    primary: theme?.primary || '#007bff',
    secondary: theme?.secondary || '#6c757d',
    background: theme?.background || '#ffffff',
    surface: theme?.surface || '#ffffff',
    text: theme?.text || '#212529',
    border: theme?.border || '#dee2e6',
    success: theme?.success || '#28a745',
    error: theme?.error || '#dc3545',
    warning: theme?.warning || '#ffc107',
    opacity: theme?.opacity || 0.9
  };


  const formatPhoneBY = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    let rest = digits;
    if (rest.startsWith('375')) rest = rest.slice(3);
    else if (rest.startsWith('80')) rest = rest.slice(2);
    let res = '+375';
    if (rest.length > 0) res += ' ' + rest.slice(0, 2);
    if (rest.length > 2) res += ' ' + rest.slice(2, 5);
    if (rest.length > 5) res += '-' + rest.slice(5, 7);
    if (rest.length > 7) res += '-' + rest.slice(7, 9);
    return res;
  };
  const isValidPhoneBY = (value) => {
    const re = /^\+375\s?\d{2}\s?\d{3}[-\s]?\d{2}[-\s]?\d{2}$/;
    return re.test(String(value || '').trim());
  };
  const [menuId, setMenuId] = useState('');
  const buildMenuUrl = () => {
    // Проверяем, находимся ли мы на субдомене
    const isSubdomain = window.location.hostname.includes('.menu.qr-code.by') && 
                       !window.location.hostname.startsWith('menu.qr-code.by');
    
    if (isSubdomain) {
      // На субдомене - используем относительный путь
      const params = [];
      
      if (menuId) {
        params.push(`menuId=${encodeURIComponent(menuId)}`);
      }
      
      // Для заказов со столов добавляем параметр table
      if (orderType === 'dine_in' && tableNumber) {
        params.push(`table=${encodeURIComponent(tableNumber)}`);
      }
      
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      return `/${queryString}`;
    } else {
      // На основном домене - используем полный путь
      let url = `/menu/${restaurant?._id || ''}`;
      const params = [];
      
      if (menuId) {
        params.push(`menuId=${encodeURIComponent(menuId)}`);
      }
      
      // Для заказов со столов добавляем параметр table
      if (orderType === 'dine_in' && tableNumber) {
        params.push(`table=${encodeURIComponent(tableNumber)}`);
      }
      
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      
      return url;
    }
  };

  // Update delivery time approval
  const updateDeliveryTimeApproval = async (approved) => {
    if (!order?._id) return;
    
    try {
      const response = await axios.put(`/api/orders/${order._id}/delivery-time-approval`, {
        approved: approved,
        callBack: callBack
      });
      
      if (response.data.success) {
        setDeliveryTimeApproved(approved);
        setShowDeliveryTimeApproval(false);
        
        // Update order summary
        setOrderSummary(prev => ({
          ...prev,
          deliveryTimeApproved: approved,
          callBack: callBack
        }));
        
        // For ExpressPay delivery orders, trigger QR generation after approval
        if (approved && isDeliveryMode() && orderSummary?.paymentProvider === 'expresspay') {
          try {
            const res = await axios.post('/api/expresspay/invoice', { orderId: order._id });
            const data = res.data || {};
            const inv = String(data.invoiceNo || data.InvoiceNo || data.invoice?.InvoiceNo || '');
            if (inv) {
              setInvoiceNo(inv);
              setPaymentUrl(data.paymentUrl || '');
              setPaymentDebug(data.attempts || null);
              setShowInvoice(true);
              
              // Try to get payment URL from QR image headers
              if (!data.paymentUrl) {
                try {
                  const qrResponse = await fetch(`/api/expresspay/invoice/${encodeURIComponent(inv)}/qr`);
                  const paymentUrlFromHeader = qrResponse.headers.get('X-Payment-Url');
                  if (paymentUrlFromHeader) {
                    setPaymentUrl(paymentUrlFromHeader);
                  }
                } catch (error) {
                  console.log('Failed to get payment URL from QR headers:', error.message);
                }
              }
              
              // Start polling payment status each 4s
              try { if (statusTimer) clearInterval(statusTimer); } catch(e) {}
              const t = setInterval(async () => {
                try {
                  const r = await axios.get(`/api/expresspay/status/${order._id}`);
                  if (r.data?.paymentStatus === 'completed') {
                    clearInterval(t);
                    setStatusTimer(null);
                    setPaid(true);
                    setTimeout(() => {
                      setShowInvoice(false);
                      navigate(buildMenuUrl());
                    }, 1800);
                  } else if (r.data?.paymentStatus === 'failed') {
                    clearInterval(t);
                    setStatusTimer(null);
                    setPaymentFailed(true);
                    setTimeout(() => {
                      setShowInvoice(false);
                      navigate(buildMenuUrl());
                    }, 1800);
                  }
                } catch(e) {
                  console.error('Error polling payment status:', e);
                }
              }, 4000);
              setStatusTimer(t);
            }
          } catch (error) {
            console.error('Error creating ExpressPay invoice after approval:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error updating delivery time approval:', error);
      setError('Ошибка при обновлении согласия на время доставки');
    }
  };
  
  // Функция для определения режима доставки
  const isDeliveryMode = () => {
    const savedMenu = localStorage.getItem('menu');
    const parsed = savedMenu ? JSON.parse(savedMenu) : null;
    return parsed?.mode === 'delivery' || orderType === 'delivery';
  };

  // Загрузка активных акций
  const fetchPromotions = useCallback(async () => {
    const savedMenu = localStorage.getItem('menu');
    const parsed = savedMenu ? JSON.parse(savedMenu) : null;
    if (!parsed?._id) return;

    try {
      const response = await axios.get(`/api/promotions/menu/${parsed._id}/active`);
      setPromotions(response.data);
    } catch (error) {
      console.error('Error fetching promotions:', error);
    }
  }, []);

  const freeDeliveryPromotion = useMemo(
    () => promotions.find((p) => p?.type === 'free_delivery') || null,
    [promotions]
  );

  // Функция для расчета скидок
  const calculatePromotions = useCallback(() => {
    if (!promotions.length || !cart.length) {
      setAppliedPromotions([]);
      return;
    }

    const applicablePromotions = [];
    const cartTotal = cart.reduce((sum, item) => {
      try {
        // Используем данные из localStorage, как в CustomerMenu.js
        const savedMenu = localStorage.getItem('menu');
        const parsed = savedMenu ? JSON.parse(savedMenu) : null;
        const sourceItem = parsed?.items?.find?.((it) => it._id === item._id);
        
        // Simple addons total
        const addonsTotal = (item.addons || []).reduce((s, name) => {
          const found = sourceItem?.addons?.find(a => a.name === name);
          const price = found ? Number(found.price) : 0;
          return s + price;
        }, 0);
        
        // Addon groups total (from item's addonGroups)
        const groupsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
          const group = sourceItem?.addonGroups?.find(g => g.name === gname);
          if (!group) return acc;
          const optsByName = new Map(group.options.map(o => [o.name, o]));
          const add = (list || []).reduce((s, nm) => {
            const price = optsByName.get(nm)?.price || 0;
            return s + price;
          }, 0);
          return acc + add;
        }, 0);
        
        // Group options total (from GroupOption model) - only if not already counted in groupsTotal
        const groupOptionsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
          // Skip if this group is already counted in groupsTotal
          const group = sourceItem?.addonGroups?.find(g => g.name === gname);
          if (group) {
            return acc; // Already counted in groupsTotal
          }
          
          if (!sourceItem?.groupOptionIds) {
            return acc;
          }
          
          // Find group option by name
          const savedGroupOptions = localStorage.getItem('groupOptionsSnapshot');
          const parsedGroupOptions = savedGroupOptions ? JSON.parse(savedGroupOptions) : [];
          const groupOption = parsedGroupOptions.find(g => g.name === gname);
          if (!groupOption) {
            return acc;
          }
          
          const optsByName = new Map(groupOption.options.map(o => [o.name, o]));
          const add = (list || []).reduce((s, nm) => {
            const price = optsByName.get(nm)?.price || 0;
            return s + price;
          }, 0);
          return acc + add;
        }, 0);
        
        const unit = Number(item.price) + addonsTotal + groupsTotal + groupOptionsTotal;
        const itemTotal = unit * item.quantity;
        
        return sum + itemTotal;
      } catch (e) {
        console.error('Error calculating cart total for item:', item._id, e);
        return sum + Number(item.price) * item.quantity;
      }
    }, 0);

    promotions.forEach(promotion => {
      const minAmount = promotion.type === 'free_delivery'
        ? Number(promotion.minDeliveryAmount || 0)
        : Number(promotion.minOrderAmount || 0);
      if (minAmount > 0 && cartTotal < minAmount) return;

      let discount = 0;
      let message = '';

      switch (promotion.type) {
        case 'percentage':
          // Фильтруем товары по категориям для расчета скидки
          const percentageApplicableItems = cart.filter(item => 
            promotion.applicableCategories.length === 0 || 
            promotion.applicableCategories.includes(item.category)
          );
          
          const percentageCartTotal = percentageApplicableItems.reduce((sum, item) => {
            const itemPrice = item.price;
            const addonPrice = Object.entries(item.addonGroups || {}).reduce((addonSum, [groupName, addons]) => {
              return addonSum + (addons || []).reduce((addonGroupSum, addon) => addonGroupSum + (addon.price || 0), 0);
            }, 0);
            return sum + (itemPrice + addonPrice) * item.quantity;
          }, 0);
          
          discount = (percentageCartTotal * promotion.discountValue) / 100;
          if (promotion.maxDiscountAmount && discount > promotion.maxDiscountAmount) {
            discount = promotion.maxDiscountAmount;
          }
          message = `Скидка ${promotion.discountValue}%`;
          break;

        case 'fixed_amount':
          // Фильтруем товары по категориям для расчета скидки
          const fixedApplicableItems = cart.filter(item => 
            promotion.applicableCategories.length === 0 || 
            promotion.applicableCategories.includes(item.category)
          );
          
          const fixedCartTotal = fixedApplicableItems.reduce((sum, item) => {
            const itemPrice = item.price;
            const addonPrice = Object.entries(item.addonGroups || {}).reduce((addonSum, [groupName, addons]) => {
              return addonSum + (addons || []).reduce((addonGroupSum, addon) => addonGroupSum + (addon.price || 0), 0);
            }, 0);
            return sum + (itemPrice + addonPrice) * item.quantity;
          }, 0);
          
          discount = promotion.discountValue;
          if (discount > fixedCartTotal) {
            discount = fixedCartTotal;
          }
          message = `Скидка ${promotion.discountValue} BYN`;
          break;

        case 'buy_x_get_y':
          const applicableItems = cart.filter(item => {
            // Проверяем привязку к конкретным товарам
            const isItemApplicable = promotion.applicableItems.length === 0 || 
              promotion.applicableItems.includes(item._id);
            
            // Проверяем привязку к категориям
            const isCategoryApplicable = promotion.applicableCategories.length === 0 || 
              promotion.applicableCategories.includes(item.category);
            
            return isItemApplicable && isCategoryApplicable;
          });
          
          // Подсчитываем общее количество товаров (с учетом количества)
          const totalItemQuantity = applicableItems.reduce((sum, item) => sum + item.quantity, 0);
          
          if (totalItemQuantity >= promotion.buyQuantity) {
            // Создаем массив всех товаров с учетом их количества
            const allItems = [];
            applicableItems.forEach(item => {
              for (let i = 0; i < item.quantity; i++) {
                allItems.push({
                  _id: item._id,
                  name: item.name,
                  price: item.price
                });
              }
            });
            
            const specialPrice = promotion.specialPrice && promotion.specialPrice > 0 ? promotion.specialPrice : 0;
            
            // Сортируем товары по цене (от дешевых к дорогим) для правильного применения скидки
            const sortedAllItems = [...allItems].sort((a, b) => a.price - b.price);
            
            // Вычисляем количество полных циклов акции
            const totalGroups = Math.floor(sortedAllItems.length / promotion.buyQuantity);
            
            // Применяем скидку к getQuantity самым дешевым товарам за каждый полный цикл
            const itemsToDiscount = Math.min(totalGroups * promotion.getQuantity, sortedAllItems.length);
            for (let j = 0; j < itemsToDiscount; j++) {
              const cheapestItem = sortedAllItems[j];
              if (cheapestItem.price > specialPrice) {
                discount += cheapestItem.price - specialPrice;
              }
            }
            
            if (promotion.specialPrice && promotion.specialPrice > 0) {
              message = `Купи ${promotion.buyQuantity}, получи ${promotion.getQuantity} по ${promotion.specialPrice} BYN`;
            } else {
              message = `Купи ${promotion.buyQuantity}, получи ${promotion.getQuantity} бесплатно`;
            }
          }
          break;

        case 'free_delivery':
          discount = 0;
          message = 'Бесплатная доставка';
          break;
      }

      if (discount > 0 || promotion.type === 'free_delivery') {
        applicablePromotions.push({
          ...promotion,
          discount,
          message
        });
      }
    });

    setAppliedPromotions(applicablePromotions);
  }, [promotions, cart]);

  // Загружаем акции при монтировании компонента
  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  // Пересчитываем акции при изменении корзины или акций
  useEffect(() => {
    calculatePromotions();
  }, [calculatePromotions]);

  const handleOtherPayment = async (method) => {
    // Prevent multiple submissions
    if (isOrderSubmitted) {
      return;
    }
    
    // Save delivery details to localStorage for persistence
    if (isDeliveryMode()) {
      localStorage.setItem('deliveryAddress', deliveryAddress);
      localStorage.setItem('deliveryEntrance', deliveryEntrance);
      localStorage.setItem('deliveryFloor', deliveryFloor);
      localStorage.setItem('customerName', customerName);
      localStorage.setItem('customerPhone', customerPhone);
    }
    
    // Save special instructions to localStorage
    localStorage.setItem('specialInstructions', specialInstructions);
    
    // Validate required fields before payment
    if (cart.length === 0) {
      setError('Ваша корзина пуста');
      return;
    }

    if (orderType === 'dine_in' && !isDeliveryMode()) {
      if (!tableNumber) {
        setError('Номер стола не был передан из QR-кода. Пожалуйста, отсканируйте QR-код стола заново.');
        return;
      }
    } else if (isDeliveryMode()) {
      if (!deliveryAddress || !customerName || !customerPhone) {
        setError('Для доставки укажите адрес, имя и телефон');
        return;
      }
      
      // Проверка минимальной суммы заказа для доставки
      const cartTotal = cart.reduce((total, item) => {
        const addonsTotal = (item.addons || []).reduce((sum, addon) => sum + (addon.price || 0), 0);
        
        // Правильная обработка addonGroups (может быть объектом или массивом)
        let groupsTotal = 0;
        if (item.addonGroups) {
          if (Array.isArray(item.addonGroups)) {
            groupsTotal = item.addonGroups.reduce((sum, group) => {
              return sum + (group.options || []).reduce((groupSum, option) => groupSum + (option.price || 0), 0);
            }, 0);
          } else if (typeof item.addonGroups === 'object') {
            // Если это объект, то это уже обработанные данные из корзины
            groupsTotal = Object.values(item.addonGroups).reduce((sum, group) => {
              if (Array.isArray(group)) {
                return sum + group.reduce((groupSum, option) => groupSum + (option.price || 0), 0);
              }
              return sum;
            }, 0);
          }
        }
        
        const unit = Number(item.price) + addonsTotal + groupsTotal;
        return total + (unit * item.quantity);
      }, 0);
      
      const menu = JSON.parse(localStorage.getItem('menu') || '{}');
      const deliverySettings = menu?.branding?.deliverySettings;
      
    }

    try {
      // Create order data directly from current state
      const orderData = {
        restaurantId: restaurant?._id,
        orderType,
        tableNumber: orderType === 'dine_in' ? String(tableNumber) : undefined,
        specialInstructions,
        items: cart.map(item => ({
          menuItemId: item._id,
          quantity: item.quantity,
          addons: Array.isArray(item.addons) ? item.addons : [],
          addonGroups: item.addonGroups && typeof item.addonGroups === 'object' && !Array.isArray(item.addonGroups) 
            ? Object.entries(item.addonGroups || {}).map(([name, options]) => ({ name, options })) 
            : Array.isArray(item.addonGroups) ? item.addonGroups : []
        })),
        totalAmount: getCartTotal(),
        paymentProvider: method,
        deliveryAddress: isDeliveryMode() ? deliveryAddress : undefined,
        deliveryEntrance: isDeliveryMode() ? deliveryEntrance : undefined,
        deliveryFloor: isDeliveryMode() ? deliveryFloor : undefined,
        customerName: isDeliveryMode() ? customerName : undefined,
        customerPhone: isDeliveryMode() ? customerPhone : undefined,
        menuId: menuId || undefined
      };
      
      // Create order in database
      const orderRes = await axios.post('/api/orders', orderData);
      const orderToPay = orderRes.data;
      setOrder(orderToPay);
      setOrderStatus('pending');
      setIsOrderSubmitted(true);
      
      // Save cart details for order summary before clearing
      const summaryData = {
        cart: cart,
        orderType: orderType,
        tableNumber: tableNumber,
        deliveryAddress: deliveryAddress,
        deliveryEntrance: deliveryEntrance,
        deliveryFloor: deliveryFloor,
        customerName: customerName,
        customerPhone: customerPhone,
        specialInstructions: specialInstructions,
        totalAmount: getCartTotal(),
        paymentProvider: method,
        // Persist minimal menu payment config for reloads
        menu: savedMenuForPayment
          ? {
              _id: savedMenuForPayment._id,
              mode: savedMenuForPayment.mode,
              paymentOptions: savedMenuForPayment.paymentOptions,
              expressPayToken: savedMenuForPayment.expressPayToken
            }
          : undefined
      };
      localStorage.setItem('orderSummary', JSON.stringify(summaryData));
      setOrderSummary(summaryData);
      
      // Clear cart after successful order creation
      localStorage.removeItem('cart');
      
      // Order created successfully
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка создания заказа');
    }
  };
  
  // Polling for order status changes
  useEffect(() => {
    if (order && order._id && (orderStatus === 'pending' || orderStatus === 'accepted')) {
      const pollOrderStatus = async () => {
        try {
          const response = await axios.get(`/api/orders/${order._id}/status`);
          const currentStatus = response.data.status;
          
          if (currentStatus !== orderStatus) {
            setOrderStatus(currentStatus);
            setShowStatusMessage(true);
            
            // Update orderSummary with deliveryTime if available
            if (response.data.deliveryTime) {
              setOrderSummary(prev => ({
                ...prev,
                deliveryTime: response.data.deliveryTime
              }));
            }
            
            // Update deliveryTimeApproved if available
            if (response.data.deliveryTimeApproved !== undefined) {
              setDeliveryTimeApproved(response.data.deliveryTimeApproved);
            }
            
            // Update callBack if available
            if (response.data.callBack !== undefined) {
              setCallBack(response.data.callBack);
            }
            
            // For ExpressPay orders, create invoice when order is accepted AND delivery time is approved
            if (currentStatus === 'accepted' && orderSummary?.paymentProvider === 'expresspay') {
              // For delivery orders, wait for delivery time approval
              if (isDeliveryMode()) {
                if (deliveryTimeApproved === true) {
                  // Delivery time approved - create invoice
              try {
                const res = await axios.post('/api/expresspay/invoice', { orderId: order._id });
                const data = res.data || {};
                const inv = String(data.invoiceNo || data.InvoiceNo || data.invoice?.InvoiceNo || '');
                if (inv) {
                  setInvoiceNo(inv);
                  setPaymentUrl(data.paymentUrl || '');
                  setPaymentDebug(data.attempts || null);
                  setShowInvoice(true);
                  
                  // Try to get payment URL from QR image headers
                  if (!data.paymentUrl) {
                    try {
                      const qrResponse = await fetch(`/api/expresspay/invoice/${encodeURIComponent(inv)}/qr`);
                      const paymentUrlFromHeader = qrResponse.headers.get('X-Payment-Url');
                      if (paymentUrlFromHeader) {
                        console.log('✅ Found payment URL in QR headers:', paymentUrlFromHeader);
                        setPaymentUrl(paymentUrlFromHeader);
                      }
                    } catch (error) {
                      console.log('Failed to get payment URL from QR headers:', error.message);
                    }
                  }
                  
                  // Start polling payment status each 4s
                  try { if (statusTimer) clearInterval(statusTimer); } catch(e) {}
                  const t = setInterval(async () => {
                    try {
                      const r = await axios.get(`/api/expresspay/status/${order._id}`);
                      if (r.data?.paymentStatus === 'completed') {
                        clearInterval(t);
                        setStatusTimer(null);
                        setPaid(true);
                        setTimeout(() => {
                          setShowInvoice(false);
                          navigate(buildMenuUrl());
                        }, 1800);
                      } else if (r.data?.paymentStatus === 'failed') {
                        console.log('❌ Payment failed detected, updating UI');
                        clearInterval(t);
                        setStatusTimer(null);
                        setPaymentFailed(true);
                        setTimeout(() => {
                          setShowInvoice(false);
                          navigate(buildMenuUrl());
                        }, 1800);
                      } else {
                        console.log('Payment status:', r.data?.paymentStatus);
                      }
                    } catch(e) {
                      console.error('Error polling payment status:', e);
                    }
                  }, 4000);
                  setStatusTimer(t);
                }
              } catch (error) {
                console.error('Error creating ExpressPay invoice:', error);
                  }
                }
                // If deliveryTimeApproved is false or null, don't create invoice yet
              } else {
                // For dine-in orders, create invoice immediately
                try {
                  const res = await axios.post('/api/expresspay/invoice', { orderId: order._id });
                  const data = res.data || {};
                  const inv = String(data.invoiceNo || data.InvoiceNo || data.invoice?.InvoiceNo || '');
                  if (inv) {
                    setInvoiceNo(inv);
                    setPaymentUrl(data.paymentUrl || '');
                    setPaymentDebug(data.attempts || null);
                    setShowInvoice(true);
                    
                    // Try to get payment URL from QR image headers
                    if (!data.paymentUrl) {
                      try {
                        const qrResponse = await fetch(`/api/expresspay/invoice/${encodeURIComponent(inv)}/qr`);
                        const paymentUrlFromHeader = qrResponse.headers.get('X-Payment-Url');
                        if (paymentUrlFromHeader) {
                          console.log('✅ Found payment URL in QR headers:', paymentUrlFromHeader);
                          setPaymentUrl(paymentUrlFromHeader);
                        }
                      } catch (error) {
                        console.log('Failed to get payment URL from QR headers:', error.message);
                      }
                    }
                    
                    // Start polling payment status each 4s
                    try { if (statusTimer) clearInterval(statusTimer); } catch(e) {}
                    const t = setInterval(async () => {
                      try {
                        const r = await axios.get(`/api/expresspay/status/${order._id}`);
                        if (r.data?.paymentStatus === 'completed') {
                          clearInterval(t);
                          setStatusTimer(null);
                          setPaid(true);
                          setTimeout(() => {
                            setShowInvoice(false);
                            navigate(buildMenuUrl());
                          }, 1800);
                        } else if (r.data?.paymentStatus === 'failed') {
                          console.log('❌ Payment failed detected, updating UI');
                          clearInterval(t);
                          setStatusTimer(null);
                          setPaymentFailed(true);
                          setTimeout(() => {
                            setShowInvoice(false);
                            navigate(buildMenuUrl());
                          }, 1800);
                        } else {
                          console.log('Payment status:', r.data?.paymentStatus);
                        }
                      } catch(e) {
                        console.error('Error polling payment status:', e);
                      }
                    }, 4000);
                    setStatusTimer(t);
                  }
                } catch (error) {
                  console.error('Error creating ExpressPay invoice:', error);
                }
              }
            }
            
            // Stop polling if order is rejected or completed
            if (currentStatus === 'rejected' || currentStatus === 'completed') {
              // Redirect to menu after showing status message
              setTimeout(() => {
                navigate(buildMenuUrl());
              }, 3000); // Увеличена задержка до 5 секунд
            }
            
            // Redirect to menu immediately when order is accepted (except ExpressPay)
            if (currentStatus === 'accepted' && orderSummary?.paymentProvider !== 'expresspay') {
              setTimeout(() => {
                navigate(buildMenuUrl());
              }, 30000); // Redirect after 30 seconds for accepted orders
            }
          }
        } catch (error) {
          console.error('Error polling order status:', error);
        }
      };
      
      // Use exponential backoff to avoid rate limiting
      let pollCount = 0;
      let currentInterval = 2000; // Start with 2 seconds
      const maxInterval = 30000; // Max 30 seconds
      const maxPolls = 60; // Stop after 60 polls (about 5 minutes)
      
      const scheduleNextPoll = () => {
        if (pollCount >= maxPolls) {
          console.log('Stopping order status polling after max attempts');
          return;
        }
        
        setTimeout(() => {
          pollOrderStatus().then(() => {
            pollCount++;
            
            // Exponential backoff: increase interval gradually
            if (pollCount > 5) {
              currentInterval = Math.min(currentInterval * 1.2, maxInterval);
            }
            
            // Continue polling if still pending/accepted
            if (pollCount < maxPolls && (orderStatus === 'pending' || orderStatus === 'accepted')) {
              scheduleNextPoll();
            }
          }).catch((error) => {
            // If 429 error, increase interval significantly
            if (error.response?.status === 429) {
              currentInterval = Math.min(currentInterval * 2, maxInterval);
              console.log(`Rate limited, increasing poll interval to ${currentInterval}ms`);
            }
            pollCount++;
            
            // Continue polling even after errors
            if (pollCount < maxPolls && (orderStatus === 'pending' || orderStatus === 'accepted')) {
              scheduleNextPoll();
            }
          });
        }, currentInterval);
      };
      
      // Start polling
      scheduleNextPoll();
      
      // Cleanup function
      return () => {
        pollCount = maxPolls; // Stop polling
      };
    }
  }, [order, orderStatus, navigate, orderSummary]);
  
  // Check order status on component mount if order exists
  useEffect(() => {
    if (order && order._id && orderStatus === 'pending') {
      const checkInitialStatus = async () => {
        try {
          const response = await axios.get(`/api/orders/${order._id}/status`);
          const currentStatus = response.data.status;
          
          // Only update if status is different from pending
          if (currentStatus !== 'pending') {
            setOrderStatus(currentStatus);
            setShowStatusMessage(true);
            
            // Update orderSummary with deliveryTime if available
            if (response.data.deliveryTime) {
              setOrderSummary(prev => ({
                ...prev,
                deliveryTime: response.data.deliveryTime
              }));
            }
            
            // Update deliveryTimeApproved if available
            if (response.data.deliveryTimeApproved !== undefined) {
              setDeliveryTimeApproved(response.data.deliveryTimeApproved);
            }
            
            // Update callBack if available
            if (response.data.callBack !== undefined) {
              setCallBack(response.data.callBack);
            }
            
            // If order is already rejected or completed, show message and redirect
            if (currentStatus === 'rejected' || currentStatus === 'completed') {
              setTimeout(() => {
                navigate(buildMenuUrl());
              }, 5000); // Give more time to read the message
            }
            
            // If order is already accepted, redirect to menu (except ExpressPay)
            if (currentStatus === 'accepted' && orderSummary?.paymentProvider !== 'expresspay') {
              setTimeout(() => {
                navigate(buildMenuUrl());
              }, 30000); // Redirect after 30 seconds for accepted orders
            }
          }
        } catch (error) {
          console.error('Error checking initial order status:', error);
        }
      };
      
      checkInitialStatus();
    }
  }, [order, orderStatus, navigate]);
  
  useEffect(() => {
    // Reset order submission state when component mounts
    setIsOrderSubmitted(false);
    
    // Load checkout context from localStorage
    const savedCart = localStorage.getItem('cart');
    const savedRestaurant = localStorage.getItem('restaurant');
    const savedMenu = localStorage.getItem('menu');
    const savedTableNumber = localStorage.getItem('tableNumber');
    const savedOrderType = localStorage.getItem('orderType');
    const savedDeliveryAddress = localStorage.getItem('deliveryAddress');
    const savedDeliveryEntrance = localStorage.getItem('deliveryEntrance');
    const savedDeliveryFloor = localStorage.getItem('deliveryFloor');
    const savedCustomerName = localStorage.getItem('customerName');
    const savedCustomerPhone = localStorage.getItem('customerPhone');
    const savedMenuId = localStorage.getItem('menuId');
    const savedSpecialInstructions = localStorage.getItem('specialInstructions');
    
    
    if (savedCart) {
      const cartData = JSON.parse(savedCart);
      setCart(cartData);
    }
    if (savedRestaurant) setRestaurant(JSON.parse(savedRestaurant));
    if (savedMenu) {
      const menuData = JSON.parse(savedMenu);
      // Загружаем тему из данных меню (как в CustomerMenu)
      if (menuData.branding?.theme) {
        setTheme(menuData.branding.theme);
      }
    }
    if (savedTableNumber) setTableNumber(savedTableNumber);
    if (savedOrderType) {
      setOrderType(savedOrderType);
    }
    if (savedDeliveryAddress) setDeliveryAddress(savedDeliveryAddress);
    if (savedDeliveryEntrance) setDeliveryEntrance(savedDeliveryEntrance);
    if (savedDeliveryFloor) setDeliveryFloor(savedDeliveryFloor);
    if (savedCustomerName) setCustomerName(savedCustomerName);
    if (savedCustomerPhone) setCustomerPhone(savedCustomerPhone);
    if (savedMenuId) setMenuId(savedMenuId);
    if (savedSpecialInstructions) setSpecialInstructions(savedSpecialInstructions);
    
    // Load orderSummary from localStorage if exists
    const savedOrderSummary = localStorage.getItem('orderSummary');
    if (savedOrderSummary) {
      setOrderSummary(JSON.parse(savedOrderSummary));
    }
    
  }, []);
  
  const getCartTotal = useCallback(() => {
    const cartTotal = cart.reduce((total, item) => {
      try {
        // Используем данные из localStorage, как в CustomerMenu.js
        const savedMenu = localStorage.getItem('menu');
        const parsed = savedMenu ? JSON.parse(savedMenu) : null;
        const sourceItem = parsed?.items?.find?.((it) => it._id === item._id);
        
        // Получаем GroupOptions из localStorage
        const savedGroupOptions = localStorage.getItem('groupOptionsSnapshot');
        const parsedGroupOptions = savedGroupOptions ? JSON.parse(savedGroupOptions) : [];
        
        // Simple addons total
        const addonsTotal = (item.addons || []).reduce((s, name) => {
          const found = sourceItem?.addons?.find(a => a.name === name);
          const price = found ? Number(found.price) : 0;
          return s + price;
        }, 0);
        
        // Addon groups total (from item's addonGroups)
        const groupsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
          const group = sourceItem?.addonGroups?.find(g => g.name === gname);
          if (!group) return acc;
          const optsByName = new Map(group.options.map(o => [o.name, o]));
          const add = (list || []).reduce((s, nm) => {
            const price = optsByName.get(nm)?.price || 0;
            return s + price;
          }, 0);
          return acc + add;
        }, 0);
        
        // Group options total (from GroupOption model) - only if not already counted in groupsTotal
        const groupOptionsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
          // Skip if this group is already counted in groupsTotal
          const group = sourceItem?.addonGroups?.find(g => g.name === gname);
          if (group) {
            return acc; // Already counted in groupsTotal
          }
          
          if (!sourceItem?.groupOptionIds) {
            return acc;
          }
          
          // Find group option by name
          const savedGroupOptions = localStorage.getItem('groupOptionsSnapshot');
          const parsedGroupOptions = savedGroupOptions ? JSON.parse(savedGroupOptions) : [];
          const groupOption = parsedGroupOptions.find(g => g.name === gname);
          if (!groupOption) {
            return acc;
          }
          
          const optsByName = new Map(groupOption.options.map(o => [o.name, o]));
          const add = (list || []).reduce((s, nm) => {
            const price = optsByName.get(nm)?.price || 0;
            return s + price;
          }, 0);
          return acc + add;
        }, 0);
        
        const unit = Number(item.price) + addonsTotal + groupsTotal + groupOptionsTotal;
        const itemTotal = unit * item.quantity;
        
        return total + itemTotal;
      } catch (e) {
        console.error('Error calculating cart total for item:', item._id, e);
        return total + Number(item.price) * item.quantity;
      }
    }, 0);
    
    // Добавляем стоимость доставки для заказов на доставку
    if (isDeliveryMode()) {
      // Приоритет: акция free_delivery (цифры должны меняться при редактировании акций)
      if (freeDeliveryPromotion) {
        const minAmount = Number(freeDeliveryPromotion.minDeliveryAmount || 0);
        const fee = Number(freeDeliveryPromotion.deliveryFee || 0);
        if (fee > 0 && minAmount > 0 && cartTotal < minAmount) {
          return cartTotal + fee;
        }
        return cartTotal;
      }

      // Фолбэк: настройки доставки меню
      const savedMenu = localStorage.getItem('menu');
      const parsed = savedMenu ? JSON.parse(savedMenu) : null;
      const deliverySettings = parsed?.branding?.deliverySettings;
      if (deliverySettings?.enabled && deliverySettings?.deliveryFee > 0) {
        const minOrderAmount = deliverySettings.minOrderAmount || 0;
        const shouldAddDeliveryFee = minOrderAmount > 0 && cartTotal < minOrderAmount;
        return shouldAddDeliveryFee ? cartTotal + deliverySettings.deliveryFee : cartTotal;
      }
    }
    
    return cartTotal;
  }, [cart, orderType, freeDeliveryPromotion]);
  
  const getCartTotalWithDiscounts = useCallback(() => {
    const subtotal = getCartTotal();
    const totalDiscount = appliedPromotions.reduce((sum, promotion) => sum + promotion.discount, 0);
    const finalTotal = Math.max(0, subtotal - totalDiscount);
    
    return finalTotal;
  }, [getCartTotal, appliedPromotions]);
  
  const handlePayment = async () => {
    // Prevent multiple submissions
    if (isOrderSubmitted) {
      return;
    }
    
    // Save delivery details to localStorage for persistence
    if (isDeliveryMode()) {
      localStorage.setItem('deliveryAddress', deliveryAddress);
      localStorage.setItem('deliveryEntrance', deliveryEntrance);
      localStorage.setItem('deliveryFloor', deliveryFloor);
      localStorage.setItem('customerName', customerName);
      localStorage.setItem('customerPhone', customerPhone);
    }
    
    // Save special instructions to localStorage
    localStorage.setItem('specialInstructions', specialInstructions);
    
    // Validate required fields before payment
    if (cart.length === 0) {
      setError('Ваша корзина пуста');
      return;
    }

    if (orderType === 'dine_in' && !isDeliveryMode()) {
      if (!tableNumber) {
        setError('Номер стола не был передан из QR-кода. Пожалуйста, отсканируйте QR-код стола заново.');
        return;
      }
    } else if (isDeliveryMode()) {
      if (!deliveryAddress || !customerName || !customerPhone) {
        setError('Для доставки укажите адрес, имя и телефон');
        return;
      }
    }

    try {
      setInvoiceError('');
      setInvoiceLoading(true);
      
      // Create order data directly from current state
      const orderData = {
        restaurantId: restaurant?._id,
        orderType,
        tableNumber: orderType === 'dine_in' ? String(tableNumber) : undefined,
        specialInstructions,
        items: cart.map(item => ({
          menuItemId: item._id,
          quantity: item.quantity,
          addons: Array.isArray(item.addons) ? item.addons : [],
          addonGroups: item.addonGroups && typeof item.addonGroups === 'object' && !Array.isArray(item.addonGroups) 
            ? Object.entries(item.addonGroups || {}).map(([name, options]) => ({ name, options })) 
            : Array.isArray(item.addonGroups) ? item.addonGroups : []
        })),
        totalAmount: getCartTotal(),
        paymentProvider: 'expresspay',
        deliveryAddress: isDeliveryMode() ? deliveryAddress : undefined,
        deliveryEntrance: isDeliveryMode() ? deliveryEntrance : undefined,
        deliveryFloor: isDeliveryMode() ? deliveryFloor : undefined,
        customerName: isDeliveryMode() ? customerName : undefined,
        customerPhone: isDeliveryMode() ? customerPhone : undefined,
        menuId: menuId || undefined
      };
      
      // Create order in database
      const orderRes = await axios.post('/api/orders', orderData);
      const orderToPay = orderRes.data;
      setOrder(orderToPay);
      setOrderStatus('pending');
      setIsOrderSubmitted(true);
      
      // Save cart details for order summary before clearing
      const summaryData = {
        cart: cart,
        orderType: orderType,
        tableNumber: tableNumber,
        deliveryAddress: deliveryAddress,
        deliveryEntrance: deliveryEntrance,
        deliveryFloor: deliveryFloor,
        customerName: customerName,
        customerPhone: customerPhone,
        specialInstructions: specialInstructions,
        totalAmount: getCartTotal(),
        paymentProvider: 'expresspay'
      };
      localStorage.setItem('orderSummary', JSON.stringify(summaryData));
      setOrderSummary(summaryData);
      
      // Clear cart after successful order creation
      localStorage.removeItem('cart');
      
      // Order created successfully - no modal shown yet, wait for manager confirmation
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка создания заказа');
    } finally {
      setInvoiceLoading(false);
    }
  };
  
  if (order) {
    return (
      <Container 
        className="mt-5" 
        style={{ 
          backgroundColor: colors.background,
          minHeight: '100vh',
          padding: '20px 0'
        }}
      >
        <Row className="justify-content-center">
          <Col md={8}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="mb-0" style={{ color: colors.primary }}>Заказ оформлен</h2>
              <Button 
                variant="outline-secondary" 
                onClick={() => navigate(buildMenuUrl())}
                style={{
                  borderColor: colors.border,
                  color: colors.text,
                  backgroundColor: 'transparent',
                  fontSize: '0.8rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                ✕ Закрыть
              </Button>
            </div>
            
            {orderStatus !== 'completed' && (
              <Alert 
                variant={orderStatus === 'accepted' ? 'success' : orderStatus === 'rejected' ? 'danger' : 'warning'} 
                className="text-center"
                style={{
                  backgroundColor: orderStatus === 'accepted' ? colors.success : 
                                  orderStatus === 'rejected' ? colors.error : 
                                  colors.warning,
                  borderColor: orderStatus === 'accepted' ? colors.success : 
                              orderStatus === 'rejected' ? colors.error : 
                              colors.warning,
                  color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#000000'
                }}
              >
                {orderStatus === 'accepted' ? (
                  <>
                    <h4 style={{ color: '#ffffff' }}>✅ Заказ принят!</h4>
                    <p style={{ color: '#ffffff' }}>Ваш заказ принят будет обработан в ближайшее время.</p>
                  </>
                ) : orderStatus === 'rejected' ? (
                  <>
                    <h4 style={{ color: '#ffffff' }}>❌ Заказ отклонен</h4>
                    <p style={{ color: '#ffffff' }}>К сожалению, ваш заказ был отклонен.</p>
                    <p style={{ color: '#ffffff' }}>Возможные причины: отсутствие товара, технические проблемы или другие обстоятельства.</p>
                    <p style={{ color: '#ffffff' }}>
                      <strong>Вы можете оформить новый заказ или связаться с рестораном для уточнения деталей.</strong>
                    </p>
                  </>
                ) : (
                  <>
                    <h4 style={{ color: '#000000' }}>⏳ Заказ отправлен на подтверждение</h4>
                    <p style={{ color: '#000000' }}>
                      <strong>Заказ будет обработан только после подтверждения.</strong>
                    </p>
                  </>
                )}
                <p style={{ color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#000000' }}>
                  Номер заказа: {order.orderNumber || `#${order._id.slice(-6)}`}
                </p>
                {(orderSummary?.tableNumber || tableNumber) && !isDeliveryMode() && (
                  <p style={{ color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#000000' }}>
                    Номер стола: {orderSummary?.tableNumber || tableNumber}
                  </p>
                )}
                {isDeliveryMode() && (
                  <>
                    <p style={{ color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#000000' }}>
                      Адрес доставки: {orderSummary?.deliveryAddress || deliveryAddress}
                    </p>
                    {(orderSummary?.deliveryEntrance || deliveryEntrance) && (
                      <p style={{ color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#000000' }}>
                        Подъезд: {orderSummary?.deliveryEntrance || deliveryEntrance}
                      </p>
                    )}
                    {(orderSummary?.deliveryFloor || deliveryFloor) && (
                      <p style={{ color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#000000' }}>
                        Этаж: {orderSummary?.deliveryFloor || deliveryFloor}
                      </p>
                    )}
                    <p style={{ color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#000000' }}>
                      Имя: {orderSummary?.customerName || customerName}
                    </p>
                    <p style={{ color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#000000' }}>
                      Телефон: {orderSummary?.customerPhone || customerPhone}
                    </p>
                    {orderSummary?.deliveryTime && orderStatus === 'accepted' && (
                      <div className="mb-3 mt-3">
                        <Card 
                          style={{ 
                            backgroundColor: '#f8f9fa', 
                            border: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }}
                        >
                          <Card.Body className="text-center py-4">
                            <div className="mb-3">
                              <i className="fas fa-clock text-primary" style={{ fontSize: '2rem' }}></i>
                            </div>
                            <h5 className="mb-3" style={{ color: '#333', fontWeight: '600' }}>
                              Время доставки
                            </h5>
                            <div 
                              className="mb-4 p-3 rounded" 
                              style={{ 
                                backgroundColor: '#e3f2fd', 
                                border: '2px solid #2196f3',
                                borderRadius: '8px'
                              }}
                            >
                              <h4 className="mb-0 text-primary" style={{ fontWeight: '700' }}>
                                {orderSummary.deliveryTime}
                              </h4>
                            </div>
                            
                            {deliveryTimeApproved === null && (
                              <div>
                                <p className="mb-4 text-muted" style={{ fontSize: '1.1rem' }}>
                                  Согласны ли вы с указанным временем доставки?
                                </p>
                                
                                {/* Callback Toggle */}
                                <div className="mb-4">
                                  <div 
                                    className="d-flex align-items-center justify-content-center p-3 rounded"
                                    style={{ 
                                      backgroundColor: '#f8f9fa',
                                      border: '1px solid #dee2e6',
                                      borderRadius: '10px'
                                    }}
                                  >
                                    <i className="fas fa-phone text-primary me-3" style={{ fontSize: '1.2rem' }}></i>
                                    <Form.Check
                                      type="switch"
                                      id="callback-switch"
                                      label="Вам перезвонить?"
                                      checked={callBack}
                                      onChange={(e) => setCallBack(e.target.checked)}
                                      style={{
                                        fontSize: '1rem',
                                        fontWeight: '500'
                                      }}
                                    />
                                  </div>
                                </div>
                                
                                <div className="d-flex gap-3 justify-content-center">
                                  <Button 
                                    variant="success" 
                                    size="lg"
                                    onClick={() => updateDeliveryTimeApproval(true)}
                                    style={{
                                      borderRadius: '25px',
                                      padding: '12px 30px',
                                      fontWeight: '600',
                                      fontSize: '1rem',
                                      boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
                                      border: 'none'
                                    }}
                                  >
                                    <i className="fas fa-check me-2"></i>
                                    Да, согласен
                                  </Button>
                                  <Button 
                                    variant="danger" 
                                    size="lg"
                                    onClick={() => updateDeliveryTimeApproval(false)}
                                    style={{
                                      borderRadius: '25px',
                                      padding: '12px 30px',
                                      fontWeight: '600',
                                      fontSize: '1rem',
                                      boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
                                      border: 'none'
                                    }}
                                  >
                                    <i className="fas fa-times me-2"></i>
                                    Нет, не подходит
                                  </Button>
                                </div>
                              </div>
                            )}
                            
                            {deliveryTimeApproved === true && (
                              <div className="text-center">
                                <div className="mb-3">
                                  <i className="fas fa-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                                </div>
                                <Badge 
                                  bg="success" 
                                  style={{ 
                                    fontSize: '1.1rem', 
                                    padding: '12px 24px',
                                    borderRadius: '20px',
                                    fontWeight: '600'
                                  }}
                                >
                                  ✅ Время доставки согласовано
                                </Badge>
                              </div>
                            )}
                            
                            {deliveryTimeApproved === false && (
                              <div className="text-center">
                                <div className="mb-3">
                                  <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
                                </div>
                                <Badge 
                                  bg="danger" 
                                  style={{ 
                                    fontSize: '1.1rem', 
                                    padding: '12px 24px',
                                    borderRadius: '20px',
                                    fontWeight: '600'
                                  }}
                                >
                                  ❌ Вы отказались от доставки
                                </Badge>
                                <p className="mt-3 text-muted small">
                                  Свяжитесь с клиентом для уточнения времени
                                </p>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    )}
                  </>
                )}
                {(orderSummary?.specialInstructions || specialInstructions) && (
                <p style={{ color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#000000' }}>
                    <strong>Примечания:</strong> {orderSummary?.specialInstructions || specialInstructions}
                  </p>
                )}
                <p style={{ color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#000000' }}>
                  Общая сумма: {getCartTotalWithDiscounts().toFixed(2)} BYN
                </p>
              </Alert>
            )}
            
            {/* Кнопка для возврата к меню при отклоненном заказе */}
            {orderStatus === 'rejected' && (
              <div className="text-center mb-4">
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={() => navigate(buildMenuUrl())}
                  style={{
                    backgroundColor: colors.success,
                    borderColor: colors.success,
                    color: '#ffffff',
                    fontSize: '0.8rem',
                    borderRadius: '8px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0,123,255,0.3)'
                  }}
                >
                  🛒 Оформить новый заказ
                </Button>
              </div>
            )}
            
            <Card 
              className="mb-4"
              style={{
                backgroundColor: colors.surface,
                borderColor: colors.border,
                color: colors.text,
                opacity: colors.opacity
              }}
            >
              <Card.Body>
                <Card.Title style={{ color: colors.primary }}>Сводка заказа</Card.Title>
                {(orderSummary?.tableNumber || tableNumber) && !isDeliveryMode() && (
                  <div className="mb-3">
                    <strong style={{ color: colors.text }}>Номер стола: </strong>
                    <span style={{ color: colors.primary, fontWeight: 'bold' }}>{orderSummary?.tableNumber || tableNumber}</span>
                  </div>
                )}
                {isDeliveryMode() && (
                  <div className="mb-3">
                    <div className="mb-2">
                      <strong style={{ color: colors.text }}>Адрес доставки: </strong>
                      <span style={{ color: colors.primary, fontWeight: 'bold' }}>{orderSummary?.deliveryAddress || deliveryAddress}</span>
                    </div>
                    {(orderSummary?.deliveryEntrance || deliveryEntrance) && (
                      <div className="mb-2">
                        <strong style={{ color: colors.text }}>Подъезд: </strong>
                        <span style={{ color: colors.primary, fontWeight: 'bold' }}>{orderSummary?.deliveryEntrance || deliveryEntrance}</span>
                      </div>
                    )}
                    {(orderSummary?.deliveryFloor || deliveryFloor) && (
                      <div className="mb-2">
                        <strong style={{ color: colors.text }}>Этаж: </strong>
                        <span style={{ color: colors.primary, fontWeight: 'bold' }}>{orderSummary?.deliveryFloor || deliveryFloor}</span>
                      </div>
                    )}
                    <div className="mb-2">
                      <strong style={{ color: colors.text }}>Имя: </strong>
                      <span style={{ color: colors.primary, fontWeight: 'bold' }}>{orderSummary?.customerName || customerName}</span>
                    </div>
                    <div className="mb-2">
                      <strong style={{ color: colors.text }}>Телефон: </strong>
                      <span style={{ color: colors.primary, fontWeight: 'bold' }}>{orderSummary?.customerPhone || customerPhone}</span>
                    </div>
                  </div>
                )}
                {(orderSummary?.specialInstructions || specialInstructions) && (
                  <div className="mb-2">
                    <strong style={{ color: colors.text }}>Примечания: </strong>
                    <span style={{ color: colors.primary, fontWeight: 'bold' }}>{orderSummary?.specialInstructions || specialInstructions}</span>
                  </div>
                )}
                {(orderSummary?.cart || cart).map((item, index) => (
                  <div key={`${item._id}-${index}`} className="border-bottom py-2">
                    <div className="d-flex justify-content-between">
                      <span style={{ color: colors.text }}>{item.name} (x{item.quantity})</span>
                      <span style={{ color: colors.success, fontWeight: 'bold' }}>
                        {(() => {
                          try {
                            // Используем данные из localStorage, как в CustomerMenu.js
                            const savedMenu = localStorage.getItem('menu');
                            const parsed = savedMenu ? JSON.parse(savedMenu) : null;
                            const sourceItem = parsed?.items?.find?.((it) => it._id === item._id);
                            
                            // Simple addons total
                            const addonsTotal = (item.addons || []).reduce((s, name) => {
                              const found = sourceItem?.addons?.find(a => a.name === name);
                              return s + (found ? Number(found.price) : 0);
                            }, 0);
                            
                            // Addon groups total (from item's addonGroups)
                            const groupsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
                              const group = sourceItem?.addonGroups?.find(g => g.name === gname);
                              if (!group) return acc;
                              const optsByName = new Map(group.options.map(o => [o.name, o]));
                              const add = (list || []).reduce((s, nm) => s + (optsByName.get(nm)?.price || 0), 0);
                              return acc + add;
                            }, 0);
                            
                            // Group options total (from GroupOption model) - only if not already counted in groupsTotal
                            const groupOptionsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
                              // Skip if this group is already counted in groupsTotal
                              const group = sourceItem?.addonGroups?.find(g => g.name === gname);
                              if (group) {
                                return acc; // Already counted in groupsTotal
                              }
                              
                              if (!sourceItem?.groupOptionIds) {
                                return acc;
                              }
                              
                              // Find group option by name
                              const savedGroupOptions = localStorage.getItem('groupOptionsSnapshot');
                              const parsedGroupOptions = savedGroupOptions ? JSON.parse(savedGroupOptions) : [];
                              const groupOption = parsedGroupOptions.find(g => g.name === gname);
                              if (!groupOption) {
                                return acc;
                              }
                              
                              const optsByName = new Map(groupOption.options.map(o => [o.name, o]));
                              const add = (list || []).reduce((s, nm) => s + (optsByName.get(nm)?.price || 0), 0);
                              return acc + add;
                            }, 0);
                            
                            const unit = Number(item.price) + addonsTotal + groupsTotal + groupOptionsTotal;
                            return (unit * item.quantity).toFixed(2) + ' BYN';
                          } catch (e) {
                            return (Number(item.price) * item.quantity).toFixed(2) + ' BYN';
                          }
                        })()}
                      </span>
                    </div>
                    {!!(item.addons && item.addons.length) && (
                      <div className="small" style={{ color: colors.secondary }}>
                        Дополнения: {item.addons.map(name => {
                          try {
                            // Используем данные из localStorage, как в CustomerMenu.js
                            const savedMenu = localStorage.getItem('menu');
                            const parsed = savedMenu ? JSON.parse(savedMenu) : null;
                            const sourceItem = parsed?.items?.find?.((it) => it._id === item._id);
                            const found = sourceItem?.addons?.find(a => a.name === name);
                            const price = found ? Number(found.price) : 0;
                            return price > 0 ? `${name} (+${price.toFixed(2)} BYN)` : name;
                          } catch (e) {
                            return name;
                          }
                        }).join(', ')}
                      </div>
                    )}
                    {item.addonGroups && Object.keys(item.addonGroups || {}).length > 0 && (
                      <div className="small" style={{ color: colors.secondary }}>
                        {Object.entries(item.addonGroups || {}).map(([g, list], groupIndex) => (
                          <div key={`${g}-${groupIndex}`}>
                            {g}: {(list || []).map(optName => {
                              try {
                                // Используем данные из localStorage, как в CustomerMenu.js
                                const savedMenu = localStorage.getItem('menu');
                                const parsed = savedMenu ? JSON.parse(savedMenu) : null;
                                const sourceItem = parsed?.items?.find?.((it) => it._id === item._id);
                                let price = 0;
                                
                                // Check in item's addonGroups first
                                const group = sourceItem?.addonGroups?.find(gr => gr.name === g);
                                if (group) {
                                  const option = group.options.find(o => o.name === optName);
                                  price = option ? Number(option.price) : 0;
                                } else {
                                  // Check in GroupOption model
                                  const savedGroupOptions = localStorage.getItem('groupOptionsSnapshot');
                                  const parsedGroupOptions = savedGroupOptions ? JSON.parse(savedGroupOptions) : [];
                                  const groupOption = parsedGroupOptions.find(gr => gr.name === g);
                                  if (groupOption) {
                                    const option = groupOption.options.find(o => o.name === optName);
                                    price = option ? Number(option.price) : 0;
                                  }
                                }
                                
                                return price > 0 ? `${optName} (+${price.toFixed(2)} BYN)` : optName;
                              } catch (e) {
                                return optName;
                              }
                            }).join(', ')}
                          </div>
                        ))}
                      </div>
                    )}
                    {typeof item.weight === 'number' && item.weight > 0 && (
                      <div className="small" style={{ color: colors.secondary }}>Вес: {item.weight} {item.weightUnit || 'г'}</div>
                    )}
                  </div>
                ))}
                <div className="mt-3">
                  {/* Показываем стоимость доставки отдельно для заказов на доставку */}
                  {isDeliveryMode() && (() => {
                    const savedMenu = localStorage.getItem('menu');
                    const parsed = savedMenu ? JSON.parse(savedMenu) : null;
                    const deliverySettings = parsed?.branding?.deliverySettings;
                    
                    if (freeDeliveryPromotion || (deliverySettings?.enabled && deliverySettings?.deliveryFee > 0)) {
                      const cartSubtotal = cart.reduce((total, item) => {
                        try {
                          const sourceItem = parsed?.items?.find?.((it) => it._id === item._id);
                          const addonsTotal = (item.addons || []).reduce((s, name) => {
                            const found = sourceItem?.addons?.find(a => a.name === name);
                            return s + (found ? Number(found.price) : 0);
                          }, 0);
                          const groupsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
                            const group = sourceItem?.addonGroups?.find(g => g.name === gname);
                            if (!group) return acc;
                            const optsByName = new Map(group.options.map(o => [o.name, o]));
                            return acc + (list || []).reduce((s, nm) => s + (optsByName.get(nm)?.price || 0), 0);
                          }, 0);
                          
                          // Group options total (from GroupOption model)
                          const groupOptionsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
                            // Skip if this group is already counted in groupsTotal
                            const group = sourceItem?.addonGroups?.find(g => g.name === gname);
                            if (group) {
                              return acc; // Already counted in groupsTotal
                            }
                            
                            if (!sourceItem?.groupOptionIds) {
                              return acc;
                            }
                            
                            // Find group option by name
                            const savedGroupOptions = localStorage.getItem('groupOptionsSnapshot');
                            const parsedGroupOptions = savedGroupOptions ? JSON.parse(savedGroupOptions) : [];
                            const groupOption = parsedGroupOptions.find(g => g.name === gname);
                            if (!groupOption) {
                              return acc;
                            }
                            
                            const optsByName = new Map(groupOption.options.map(o => [o.name, o]));
                            const add = (list || []).reduce((s, nm) => {
                              const price = optsByName.get(nm)?.price || 0;
                              return s + price;
                            }, 0);
                            return acc + add;
                          }, 0);
                          
                          const unit = Number(item.price) + addonsTotal + groupsTotal + groupOptionsTotal;
                          return total + (unit * item.quantity);
                        } catch (e) {
                          return total + Number(item.price) * item.quantity;
                        }
                      }, 0);
                      
                      const minOrderAmount = freeDeliveryPromotion
                        ? Number(freeDeliveryPromotion.minDeliveryAmount || 0)
                        : Number(deliverySettings?.minOrderAmount || 0);
                      const fee = freeDeliveryPromotion
                        ? Number(freeDeliveryPromotion.deliveryFee || 0)
                        : Number(deliverySettings?.deliveryFee || 0);
                      const isFreeDelivery = minOrderAmount === 0 || (minOrderAmount > 0 && cartSubtotal >= minOrderAmount);
                      
                      return (
                        <>
                          <div className="d-flex justify-content-between">
                            <span style={{ color: colors.text }}>Сумма заказа:</span>
                            <span style={{ color: colors.text }}>{cartSubtotal.toFixed(2)} BYN</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span style={{ color: colors.text }}>Доставка:</span>
                            <span style={{ color: isFreeDelivery ? colors.success : colors.text }}>
                              {isFreeDelivery ? 'Бесплатно' : `${fee.toFixed(2)} BYN`}
                            </span>
                          </div>
                          {isFreeDelivery && minOrderAmount > 0 && (
                            <div className="small text-success text-center mt-1">
                              Доставка бесплатна при заказе от {minOrderAmount.toFixed(2)} BYN
                            </div>
                          )}
                        </>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Отображение примененных акций */}
                  {appliedPromotions.filter(p => p.type !== 'free_delivery').length > 0 && (
                    <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${orderStatus === 'accepted' || orderStatus === 'rejected' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)'}` }}>
                      <h6 style={{ 
                        color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : colors.text, 
                        marginBottom: '1rem' 
                      }}>
                        <i className="fas fa-percent me-2" style={{ color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : colors.success }}></i>
                        Применённые акции:
                      </h6>
                      {appliedPromotions.filter(p => p.type !== 'free_delivery').map((promotion, index) => (
                        <div key={index} className="promotion-banner" style={{
                          background: orderStatus === 'accepted' || orderStatus === 'rejected' 
                            ? 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
                            : 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                          borderRadius: '12px',
                          padding: '16px',
                          margin: '8px 0',
                          boxShadow: orderStatus === 'accepted' || orderStatus === 'rejected' 
                            ? '0 4px 12px rgba(255,255,255,0.1)'
                            : '0 4px 12px rgba(0,0,0,0.1)',
                          border: orderStatus === 'accepted' || orderStatus === 'rejected' 
                            ? '1px solid rgba(255,255,255,0.2)'
                            : '1px solid rgba(255,255,255,0.3)'
                        }}>
                          <div className="promotion-header">
                            <i className={`fas ${promotion.type === 'free_delivery' ? 'fa-truck' : 'fa-percent'} promotion-icon`} style={{
                              color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#ff9800'
                            }}></i>
                            <h6 className="promotion-title" style={{
                              color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#1976d2'
                            }}>{promotion.title}</h6>
                          </div>
                          
                          <div className="promotion-details">
                            <div className="promotion-discount">
                              <i className={`fas ${promotion.type === 'free_delivery' ? 'fa-gift' : 'fa-tag'} promotion-discount-icon`} style={{
                                color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#ff9800'
                              }}></i>
                              <span className="promotion-discount-text" style={{
                                color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#1976d2'
                              }}>
                                {promotion.type === 'free_delivery' ? 'Бесплатная доставка' : `Скидка: -${promotion.discount.toFixed(2)} BYN`}
                              </span>
                            </div>
                          </div>
                          
                          {promotion.description && (
                            <div className="promotion-description">
                              <i className="fas fa-lightbulb promotion-description-icon" style={{
                                color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#ffc107'
                              }}></i>
                              <span style={{
                                color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#666'
                              }}>{promotion.description}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="d-flex justify-content-between">
                    <strong style={{ color: colors.text }}>Итого:</strong>
                      <strong style={{ color: colors.success }}>{getCartTotalWithDiscounts().toFixed(2)} BYN</strong>
                  </div>
                </div>
              </Card.Body>
            </Card>
            
            {!paymentMethod && (
            <div className="mb-4">
              <div className="text-center mb-4">
                <h5 className="fw-bold mb-2" style={{ color: colors.primary }}>Способ оплаты</h5>
                <div className="mx-auto" style={{ width: '60px', height: '3px', background: 'linear-gradient(90deg, #007bff, #0056b3)', borderRadius: '2px' }}></div>
              </div>
             
            {isDeliveryMode() ? (
                <div className="d-flex flex-column gap-2">
                  {showExpressPayDelivery && (
                  <div 
                        className={`payment-option border rounded p-3 ${paymentMethod === 'expresspay' ? 'payment-option-selected' : 'payment-option-unselected'}`}
                        onClick={() => {
                          if (paymentMethod === 'expresspay') return; // Уже выбран
                          setPaymentMethod('expresspay');
                          handlePayment();
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i className="fas fa-qrcode me-2"></i>
                          <span>QR‑Code (ЕРИП/E-POS)</span>
                        </div>
                  </div>
                  )}
                  {allowCashDelivery && (
                  <div 
                        className={`payment-option border rounded p-3 ${paymentMethod === 'cash' ? 'payment-option-selected' : 'payment-option-unselected'}`}
                        onClick={() => {
                          if (paymentMethod === 'cash') return; // Уже выбран
                          setPaymentMethod('cash');
                          handleOtherPayment('cash');
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i className="fas fa-money-bill-wave me-2"></i>
                          <span>Наличными курьеру</span>
                        </div>
                  </div>
                  )}
                  {!allowCashDelivery && !showExpressPayDelivery && (
                    <Alert variant="warning" className="mb-0">
                      Способы оплаты временно недоступны.
                    </Alert>
                  )}
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  <div 
                    className={`payment-option border rounded p-3 ${paymentMethod === 'expresspay' ? 'payment-option-selected' : 'payment-option-unselected'}`}
                    onClick={() => {
                      if (paymentMethod === 'expresspay') return; // Уже выбран
                      setPaymentMethod('expresspay');
                      handlePayment();
                    }}
                  >
                    <Form.Check
                      type="radio"
                      id="expresspay-dinein"
                      name="paymentMethod"
                      label="QR‑Code (ЕРИП/E-POS)"
                      value="expresspay"
                      checked={paymentMethod === 'expresspay'}
                      onChange={() => {}} // Пустой обработчик, так как клик обрабатывается на div
                      style={{ 
                        '--bs-form-check-input-bg': 'transparent',
                        '--bs-form-check-input-border': 'none'
                      }}
                      className="form-check-input-hidden"
                    />
                  </div>
                      <div 
                        className={`payment-option border rounded p-3 ${paymentMethod === 'onsite' ? 'payment-option-selected' : 'payment-option-unselected'}`}
                        onClick={() => {
                          if (paymentMethod === 'onsite') return; // Уже выбран
                          setPaymentMethod('onsite');
                          handleOtherPayment('onsite');
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i className="fas fa-store me-2"></i>
                          <span>На месте</span>
                        </div>
                      </div>
                    </div>
                  )}
            </div>
            )}

            {/* ExpressPay Modal - moved outside conditional rendering */}
            <Modal show={showInvoice} onHide={() => setShowInvoice(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>Оплата заказа</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {paid ? (
                  <Alert variant="success" className="mb-0 text-center">Оплата подтверждена. Возвращаемся в меню…</Alert>
                ) : paymentFailed ? (
                  <Alert variant="danger" className="mb-0 text-center">
                    <h5>❌ Оплата не прошла</h5>
                    <p>К сожалению, оплата черезР ЕРИП/E-POS Отнена.</p>
                  </Alert>
                ) : invoiceNo ? (
                  <div className="text-center">
                    <p className="mb-2">Отсканируйте QR для оплаты через ЕРИП/E-POS.</p>
                    {(() => {
                      const img = (
                        <img
                          alt="QR-счет"
                          src={`/api/expresspay/invoice/${encodeURIComponent(invoiceNo)}/qr`}
                          style={{ width: 256, height: 256, cursor: paymentUrl ? 'pointer' : 'default' }}
                        />
                      );
                      return paymentUrl ? (
                        <a href={paymentUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
                          {img}
                        </a>
                      ) : img;
                    })()}
                    <div className="mt-2 small text-muted">Номер счета: {invoiceNo}</div>
                    {paymentUrl ? (
                      <div className="mt-3">
                        <Button
                          as="a"
                          href={paymentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="primary"
                          className="w-100"
                          style={{
                            backgroundColor: colors.success,
                            borderColor: colors.success,
                            color: '#ffffff',
                            fontSize: '0.8rem',
                            borderRadius: '8px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(0,123,255,0.3)'
                          }}
                        >
                          Открыть оплату в браузере
                        </Button>
                        <div className="small text-muted mt-1">Удобно для оплаты с телефона</div>
                      </div>
                    ) : (
                      paymentDebug ? (
                        <Alert variant="warning" className="text-start mt-3">
                          <div className="fw-semibold mb-1">Ссылка не получена. Диагностика:</div>
                          <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                            {typeof paymentDebug === 'string' ? paymentDebug : JSON.stringify(paymentDebug, null, 2)}
                          </pre>
                        </Alert>
                      ) : null
                    )}
                  </div>
                ) : invoiceLoading ? (
                  <div className="text-center text-muted">Создание счета…</div>
                ) : (
                  <Alert variant="danger" className="mb-0">
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                      {typeof invoiceError === 'string' ? invoiceError : JSON.stringify(invoiceError, null, 2)}
                    </pre>
                  </Alert>
                )}
              </Modal.Body>
            </Modal>
          </Col>
        </Row>
      </Container>
    );
  }
  
  return (
    <Container 
      className="mt-5"
      style={{ 
        backgroundColor: colors.background,
        minHeight: '100vh',
        padding: '20px 0'
      }}
    >
      <style>{`
        .payment-option {
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .payment-option:hover {
          opacity: 0.8;
          transform: scale(1.02);
        }
        ${checkoutStyles}
      `}</style>
      <Row className="justify-content-center">
        <Col md={8}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0" style={{ color: colors.primary }}>Оформление заказа</h2>
            <Button 
              variant="outline-secondary" 
              onClick={() => navigate(buildMenuUrl())}
              style={{
                borderColor: colors.border,
                color: colors.text,
                backgroundColor: 'transparent',
                fontSize: '0.8rem',
                borderRadius: '8px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              ✕ Закрыть
            </Button>
          </div>
          
          {error && (
            <Alert 
              variant="danger"
              style={{
                backgroundColor: colors.error,
                borderColor: colors.error,
                color: '#ffffff'
              }}
            >
              {error}
            </Alert>
          )}
          
          <Card 
            className="mb-4"
            style={{
              backgroundColor: colors.surface,
              borderColor: colors.border,
              color: colors.text,
              opacity: colors.opacity
            }}
          >
            <Card.Body>
              <Card.Title style={{ color: colors.primary }}>Детали заказа</Card.Title>

              {orderType === 'dine_in' ? (
                <Form.Group className="mb-3">
                  <Form.Label>Номер стола</Form.Label>
                  <Form.Control
                    type="text"
                    value={tableNumber || ''}
                    disabled
                    readOnly
                    placeholder={tableNumber ? `Стол № ${tableNumber}` : 'Номер стола не указан'}
                  />
                  {!tableNumber && (
                    <Form.Text className="text-warning">
                      Номер стола должен быть передан из QR-кода
                    </Form.Text>
                  )}
                </Form.Group>
              ) : (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Адрес доставки</Form.Label>
                    <Form.Control
                      type="text"
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder="Город, улица, дом, квартира"
                    />
                  </Form.Group>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Подъезд</Form.Label>
                        <Form.Control
                          type="text"
                          value={deliveryEntrance}
                          onChange={(e) => setDeliveryEntrance(e.target.value)}
                          placeholder="Номер подъезда"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Этаж</Form.Label>
                        <Form.Control
                          type="text"
                          value={deliveryFloor}
                          onChange={(e) => setDeliveryFloor(e.target.value)}
                          placeholder="Номер этажа"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Имя</Form.Label>
                        <Form.Control
                          type="text"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          placeholder="Ваше имя"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Телефон</Form.Label>
                        <Form.Control
                          type="tel"
                          value={customerPhone}
                    onChange={(e) => {
                      const formatted = formatPhoneBY(e.target.value);
                      setCustomerPhone(formatted);
                      setPhoneError('');
                    }}
                    onBlur={() => {
                      if (customerPhone && !isValidPhoneBY(customerPhone)) {
                        setPhoneError('Введите телефон в формате +375 XX XXX-XX-XX');
                      }
                    }}
                    placeholder="+375 XX XXX-XX-XX"
                  />
                  {phoneError && <div className="text-danger small mt-1">{phoneError}</div>}
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
              
              <Form.Group className="mb-3">
                <Form.Label>Примечания:</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  placeholder="Есть ли у вас особые пожелания?"
                />
              </Form.Group>
            </Card.Body>
          </Card>
          
          <Card 
            className="mb-4"
            style={{
              backgroundColor: colors.surface,
              borderColor: colors.border,
              color: colors.text,
              opacity: colors.opacity
            }}
          >
            <Card.Body>
              <Card.Title style={{ color: colors.primary }}>Сводка заказа</Card.Title>
              {cart.length === 0 ? (
                <p style={{ color: colors.text, textAlign: 'center', marginTop: '20px' }}>Ваша корзина пуста</p>
              ) : (
                <>
                  {cart.map((item, index) => (
                    <div key={`${item._id}-${index}`} className="border-bottom py-2">
                      <div className="d-flex justify-content-between">
                        <span style={{ color: colors.text }}>{item.name} (x{item.quantity})</span>
                        <span style={{ color: colors.success, fontWeight: 'bold' }}>
                          {(() => {
                            try {
                              // Используем данные из localStorage, как в CustomerMenu.js
                              const savedMenu = localStorage.getItem('menu');
                              const parsed = savedMenu ? JSON.parse(savedMenu) : null;
                              const sourceItem = parsed?.items?.find?.((it) => it._id === item._id);
                              
                              // Simple addons total
                              const addonsTotal = (item.addons || []).reduce((s, name) => {
                                const found = sourceItem?.addons?.find(a => a.name === name);
                                return s + (found ? Number(found.price) : 0);
                              }, 0);
                              
                              // Addon groups total (from item's addonGroups)
                              const groupsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
                                const group = sourceItem?.addonGroups?.find(g => g.name === gname);
                                if (!group) return acc;
                                const optsByName = new Map(group.options.map(o => [o.name, o]));
                                const add = (list || []).reduce((s, nm) => s + (optsByName.get(nm)?.price || 0), 0);
                                return acc + add;
                              }, 0);
                              
                              // Group options total (from GroupOption model) - only if not already counted in groupsTotal
                              const groupOptionsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
                                // Skip if this group is already counted in groupsTotal
                                const group = sourceItem?.addonGroups?.find(g => g.name === gname);
                                if (group) {
                                  return acc; // Already counted in groupsTotal
                                }
                                
                                if (!sourceItem?.groupOptionIds) {
                                  return acc;
                                }
                                
                                // Find group option by name
                                const savedGroupOptions = localStorage.getItem('groupOptionsSnapshot');
                                const parsedGroupOptions = savedGroupOptions ? JSON.parse(savedGroupOptions) : [];
                                const groupOption = parsedGroupOptions.find(g => g.name === gname);
                                if (!groupOption) {
                                  return acc;
                                }
                                
                                const optsByName = new Map(groupOption.options.map(o => [o.name, o]));
                                const add = (list || []).reduce((s, nm) => s + (optsByName.get(nm)?.price || 0), 0);
                                return acc + add;
                              }, 0);
                              
                              const unit = Number(item.price) + addonsTotal + groupsTotal + groupOptionsTotal;
                              return (unit * item.quantity).toFixed(2) + ' BYN';
                            } catch (e) {
                              return (Number(item.price) * item.quantity).toFixed(2) + ' BYN';
                            }
                          })()}
                        </span>
                      </div>
                      {!!(item.addons && item.addons.length) && (
                        <div className="small" style={{ color: colors.secondary }}>
                          Дополнения: {item.addons.map(name => {
                            try {
                              // Используем данные из localStorage, как в CustomerMenu.js
                              const savedMenu = localStorage.getItem('menu');
                              const parsed = savedMenu ? JSON.parse(savedMenu) : null;
                              const sourceItem = parsed?.items?.find?.((it) => it._id === item._id);
                              const found = sourceItem?.addons?.find(a => a.name === name);
                              const price = found ? Number(found.price) : 0;
                              return price > 0 ? `${name} (+${price.toFixed(2)} BYN)` : name;
                            } catch (e) {
                              return name;
                            }
                          }).join(', ')}
                        </div>
                      )}
                      {item.addonGroups && Object.keys(item.addonGroups || {}).length > 0 && (
                        <div className="small" style={{ color: colors.secondary }}>
                          {Object.entries(item.addonGroups || {}).map(([g, list], groupIndex) => (
                            <div key={`${g}-${groupIndex}`}>
                              {g}: {(list || []).map(optName => {
                                try {
                                  // Используем данные из localStorage, как в CustomerMenu.js
                                  const savedMenu = localStorage.getItem('menu');
                                  const parsed = savedMenu ? JSON.parse(savedMenu) : null;
                                  const sourceItem = parsed?.items?.find?.((it) => it._id === item._id);
                                  let price = 0;
                                  
                                  // Check in item's addonGroups first
                                  const group = sourceItem?.addonGroups?.find(gr => gr.name === g);
                                  if (group) {
                                    const option = group.options.find(o => o.name === optName);
                                    price = option ? Number(option.price) : 0;
                                  } else {
                                    // Check in GroupOption model
                                    const savedGroupOptions = localStorage.getItem('groupOptionsSnapshot');
                                    const parsedGroupOptions = savedGroupOptions ? JSON.parse(savedGroupOptions) : [];
                                    const groupOption = parsedGroupOptions.find(gr => gr.name === g);
                                    if (groupOption) {
                                      const option = groupOption.options.find(o => o.name === optName);
                                      price = option ? Number(option.price) : 0;
                                    }
                                  }
                                  
                                  return price > 0 ? `${optName} (+${price.toFixed(2)} BYN)` : optName;
                                } catch (e) {
                                  return optName;
                                }
                              }).join(', ')}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="mt-3">
                    {/* Показываем стоимость доставки отдельно для заказов на доставку */}
                    {isDeliveryMode() && (() => {
                      const savedMenu = localStorage.getItem('menu');
                      const parsed = savedMenu ? JSON.parse(savedMenu) : null;
                      const deliverySettings = parsed?.branding?.deliverySettings;
                      
                      if (freeDeliveryPromotion || (deliverySettings?.enabled && deliverySettings?.deliveryFee > 0)) {
                        const cartSubtotal = cart.reduce((total, item) => {
                          try {
                            const sourceItem = parsed?.items?.find?.((it) => it._id === item._id);
                            const addonsTotal = (item.addons || []).reduce((s, name) => {
                              const found = sourceItem?.addons?.find(a => a.name === name);
                              return s + (found ? Number(found.price) : 0);
                            }, 0);
                            const groupsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
                              const group = sourceItem?.addonGroups?.find(g => g.name === gname);
                              if (!group) return acc;
                              const optsByName = new Map(group.options.map(o => [o.name, o]));
                              return acc + (list || []).reduce((s, nm) => s + (optsByName.get(nm)?.price || 0), 0);
                            }, 0);
                            
                            // Group options total (from GroupOption model)
                            const groupOptionsTotal = Object.entries(item.addonGroups || {}).reduce((acc, [gname, list]) => {
                              // Skip if this group is already counted in groupsTotal
                              const group = sourceItem?.addonGroups?.find(g => g.name === gname);
                              if (group) {
                                return acc; // Already counted in groupsTotal
                              }
                              
                              if (!sourceItem?.groupOptionIds) {
                                return acc;
                              }
                              
                              // Find group option by name
                              const savedGroupOptions = localStorage.getItem('groupOptionsSnapshot');
                              const parsedGroupOptions = savedGroupOptions ? JSON.parse(savedGroupOptions) : [];
                              const groupOption = parsedGroupOptions.find(g => g.name === gname);
                              if (!groupOption) {
                                return acc;
                              }
                              
                              const optsByName = new Map(groupOption.options.map(o => [o.name, o]));
                              const add = (list || []).reduce((s, nm) => {
                                const price = optsByName.get(nm)?.price || 0;
                                return s + price;
                              }, 0);
                              return acc + add;
                            }, 0);
                            
                            const unit = Number(item.price) + addonsTotal + groupsTotal + groupOptionsTotal;
                            return total + (unit * item.quantity);
                          } catch (e) {
                            return total + Number(item.price) * item.quantity;
                          }
                        }, 0);
                        
                        const minOrderAmount = freeDeliveryPromotion
                          ? Number(freeDeliveryPromotion.minDeliveryAmount || 0)
                          : Number(deliverySettings?.minOrderAmount || 0);
                        const fee = freeDeliveryPromotion
                          ? Number(freeDeliveryPromotion.deliveryFee || 0)
                          : Number(deliverySettings?.deliveryFee || 0);
                        const isFreeDelivery = minOrderAmount === 0 || (minOrderAmount > 0 && cartSubtotal >= minOrderAmount);
                        
                        return (
                          <>
                            <div className="d-flex justify-content-between">
                              <span style={{ color: colors.text }}>Сумма заказа:</span>
                              <span style={{ color: colors.text }}>{cartSubtotal.toFixed(2)} BYN</span>
                            </div>
                            <div className="d-flex justify-content-between">
                              <span style={{ color: colors.text }}>Доставка:</span>
                              <span style={{ color: isFreeDelivery ? colors.success : colors.text }}>
                                {isFreeDelivery ? 'Бесплатно' : `${fee.toFixed(2)} BYN`}
                              </span>
                            </div>
                            {isFreeDelivery && minOrderAmount > 0 && (
                              <div className="small text-success text-center mt-1">
                                Доставка бесплатна при заказе от {minOrderAmount.toFixed(2)} BYN
                              </div>
                            )}
                          </>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Отображение примененных акций */}
                    {appliedPromotions.filter(p => p.type !== 'free_delivery').length > 0 && (
                      <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${orderStatus === 'accepted' || orderStatus === 'rejected' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)'}` }}>
                        <h6 style={{ 
                          color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : colors.text, 
                          marginBottom: '1rem' 
                        }}>
                          <i className="fas fa-percent me-2" style={{ color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : colors.success }}></i>
                          Применённые акции:
                        </h6>
                        {appliedPromotions.filter(p => p.type !== 'free_delivery').map((promotion, index) => (
                          <div key={index} className="promotion-banner" style={{
                            background: orderStatus === 'accepted' || orderStatus === 'rejected' 
                              ? 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
                              : 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                            borderRadius: '12px',
                            padding: '16px',
                            margin: '8px 0',
                            boxShadow: orderStatus === 'accepted' || orderStatus === 'rejected' 
                              ? '0 4px 12px rgba(255,255,255,0.1)'
                              : '0 4px 12px rgba(0,0,0,0.1)',
                            border: orderStatus === 'accepted' || orderStatus === 'rejected' 
                              ? '1px solid rgba(255,255,255,0.2)'
                              : '1px solid rgba(255,255,255,0.3)'
                          }}>
                            <div className="promotion-header">
                              <i className={`fas ${promotion.type === 'free_delivery' ? 'fa-truck' : 'fa-percent'} promotion-icon`} style={{
                                color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#ff9800'
                              }}></i>
                              <h6 className="promotion-title" style={{
                                color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#1976d2'
                              }}>{promotion.title}</h6>
                            </div>
                            
                            <div className="promotion-details">
                              <div className="promotion-discount">
                                <i className={`fas ${promotion.type === 'free_delivery' ? 'fa-gift' : 'fa-tag'} promotion-discount-icon`} style={{
                                  color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#ff9800'
                                }}></i>
                                <span className="promotion-discount-text" style={{
                                  color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#1976d2'
                                }}>
                                  {promotion.type === 'free_delivery' ? 'Бесплатная доставка' : `Скидка: -${promotion.discount.toFixed(2)} BYN`}
                                </span>
                              </div>
                            </div>
                            
                            {promotion.description && (
                              <div className="promotion-description">
                                <i className="fas fa-lightbulb promotion-description-icon" style={{
                                  color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#ffc107'
                                }}></i>
                                <span style={{
                                  color: orderStatus === 'accepted' || orderStatus === 'rejected' ? '#ffffff' : '#666'
                                }}>{promotion.description}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="d-flex justify-content-between">
                      <strong style={{ color: colors.text }}>Итого:</strong>
                      <strong style={{ color: colors.success }}>{getCartTotalWithDiscounts().toFixed(2)} BYN</strong>
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
          
          {/* Show payment options immediately */}
          {!(orderStatus === 'accepted' && isDeliveryMode() && orderSummary?.deliveryTime) && (
            <Card 
              className="mb-4"
              style={{
                backgroundColor: colors.surface,
                borderColor: colors.border,
                color: colors.text,
                opacity: colors.opacity
              }}
            >
              <Card.Body>
                <div className="text-center mb-3">
                  <Card.Title style={{ color: colors.primary, fontSize: '1.25rem' }}>Выберите способ оплаты</Card.Title>
                </div>
                <div className="mb-4">
               
          
                  {isDeliveryMode() ? (
                    <div className="d-flex flex-column gap-2">
                      {showExpressPayDelivery && (
                      <div 
                        className={`payment-option border rounded p-3 ${paymentMethod === 'expresspay' ? 'payment-option-selected' : 'payment-option-unselected'}`}
                        onClick={() => {
                      if (paymentMethod === 'expresspay') return; // Уже выбран
                      setPaymentMethod('expresspay');
                      handlePayment();
                    }}
                      >
                        <Form.Check
                          type="radio"
                          id="expresspay-delivery-payment"
                          name="paymentMethod"
                          label="QR‑Code (ЕРИП/E-POS)"
                          value="expresspay"
                          checked={paymentMethod === 'expresspay'}
                          onChange={() => {}}
                          style={{ 
                            '--bs-form-check-input-bg': 'transparent',
                            '--bs-form-check-input-border': 'none'
                          }}
                          className="form-check-input-hidden"
                        />
                      </div>
                      )}
                      {allowCashDelivery && (
                      <div 
                        className={`payment-option border rounded p-3 ${paymentMethod === 'cash' ? 'payment-option-selected' : 'payment-option-unselected'}`}
                        onClick={() => {
                      if (paymentMethod === 'cash') return; // Уже выбран
                      setPaymentMethod('cash');
                      handleOtherPayment('cash');
                    }}
                      >
                        <Form.Check
                          type="radio"
                          id="cash-delivery-payment"
                          name="paymentMethod"
                          label="Наличными курьеру"
                          value="cash"
                          checked={paymentMethod === 'cash'}
                          onChange={() => {}}
                          style={{ 
                            '--bs-form-check-input-bg': 'transparent',
                            '--bs-form-check-input-border': 'none'
                          }}
                          className="form-check-input-hidden"
                        />
                      </div>
                      )}
                      {!allowCashDelivery && !showExpressPayDelivery && (
                        <Alert variant="warning" className="mb-0">
                          Способы оплаты временно недоступны.
                        </Alert>
                      )}
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      <div 
                        className={`payment-option border rounded p-3 ${paymentMethod === 'expresspay' ? 'payment-option-selected' : 'payment-option-unselected'}`}
                        onClick={() => {
                      if (paymentMethod === 'expresspay') return; // Уже выбран
                      setPaymentMethod('expresspay');
                      handlePayment();
                    }}
                      >
                        <Form.Check
                          type="radio"
                          id="expresspay-dinein-payment"
                          name="paymentMethod"
                          label="QR‑Code (ЕРИП/E-POS)"
                          value="expresspay"
                          checked={paymentMethod === 'expresspay'}
                          onChange={() => {}}
                          style={{ 
                            '--bs-form-check-input-bg': 'transparent',
                            '--bs-form-check-input-border': 'none'
                          }}
                          className="form-check-input-hidden"
                        />
                      </div>
                      <div 
                        className={`payment-option border rounded p-3 ${paymentMethod === 'onsite' ? 'payment-option-selected' : 'payment-option-unselected'}`}
                        onClick={() => {
                          if (paymentMethod === 'onsite') return; // Уже выбран
                          setPaymentMethod('onsite');
                          handleOtherPayment('onsite');
                        }}
                      >
                        <Form.Check
                          type="radio"
                          id="onsite-dinein-payment"
                          name="paymentMethod"
                          label="На месте"
                          value="onsite"
                          checked={paymentMethod === 'onsite'}
                          onChange={() => {}}
                          style={{ 
                            '--bs-form-check-input-bg': 'transparent',
                            '--bs-form-check-input-border': 'none'
                          }}
                          className="form-check-input-hidden"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}
         
        </Col>
      </Row>
    </Container>
  );
};

export default Checkout;